import { createAsyncThunk } from "@reduxjs/toolkit";
import { fetchData } from "../../../services/apiService";
import { getUtcDateRangeForLocalRange } from "../../../services/dateUtils";
import { showSnackbar } from "../../snackbar/Snackbar.slice";
import {
  TRANSACTION_SEARCH_API_VERSION,
  TRANSACTION_SEARCH_ENDPOINT,
  TRANSACTION_SEARCH_MAX_RETRIES,
  TRANSACTION_SEARCH_RETRY_DELAY_MS,
} from "./TransactionSearchReport.constants";
import { setSearchProgress } from "./TransactionSearchReport.slice";
import { mergeTransactionSearchResponses } from "./TransactionSearchReport.utils";

const SEARCH_TRANSACTION_REPORT = "transactionSearchReport/search";

const fetchTransactionSearchForUtcDate = async ({
  utcDate,
  merchantID,
  terminalID,
  currency,
  amount,
  exchangeName,
  token,
  acquirerData,
  wasUsed,
  acquirerId,
}) => {
  let formattedAmount = "";
  if (amount) {
    formattedAmount = Number(amount).toFixed(2).padStart(16, "0");
  }

  const data = {
    version: TRANSACTION_SEARCH_API_VERSION,
    acquirer: {
      merchantID,
      terminalID: terminalID.toUpperCase(),
      timeStamp: utcDate,
    },
    fiat: {
      currencyCode: currency,
      amount: formattedAmount,
    },
    crypto: {
      ...(exchangeName?.trim() ? { exchangeName: exchangeName.trim() } : {}),
    },
    rate: {
      requestDate: utcDate,
    },
  };

  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    acquirerName: acquirerData.acquirerName,
    acquirerID: wasUsed ? acquirerId : acquirerData.acquirerId,
  };

  const response = await fetchData({
    endpoint: TRANSACTION_SEARCH_ENDPOINT,
    data,
    headers,
    token,
  });

  if (response?.data) {
    return response.data;
  }

  throw new Error("No data received from server");
};

export const searchTransactionReport = createAsyncThunk(
  SEARCH_TRANSACTION_REPORT,
  async (
    {
      merchantID,
      terminalID,
      currency,
      amount,
      exchangeName,
      requestDate,
      token,
      acquirerData,
      wasUsed,
      acquirerId,
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const utcDates = getUtcDateRangeForLocalRange(
        requestDate,
        requestDate,
        "YYYYMMDD",
      );

      if (!utcDates.length) {
        return rejectWithValue({
          message: "Invalid request date",
          retryCount: 0,
        });
      }

      const successfulResponses = [];
      let lastRetryCount = 0;

      for (const utcDate of utcDates) {
        let attempts = 0;
        let successForDate = false;

        while (attempts < TRANSACTION_SEARCH_MAX_RETRIES && !successForDate) {
          attempts += 1;
          lastRetryCount = attempts;
          dispatch(setSearchProgress({ retryCount: attempts }));

          try {
            const responseData = await fetchTransactionSearchForUtcDate({
              utcDate,
              merchantID,
              terminalID,
              currency,
              amount,
              exchangeName,
              token,
              acquirerData,
              wasUsed,
              acquirerId,
            });
            successfulResponses.push(responseData);
            successForDate = true;
          } catch (error) {
            console.error(
              `Attempt ${attempts} failed for UTC date ${utcDate}:`,
              error,
            );
            if (attempts < TRANSACTION_SEARCH_MAX_RETRIES) {
              await new Promise((resolve) =>
                setTimeout(resolve, TRANSACTION_SEARCH_RETRY_DELAY_MS),
              );
            }
          }
        }
      }

      if (!successfulResponses.length) {
        dispatch(
          showSnackbar({
            message: "No transaction for this date",
            severity: "error",
          }),
        );
        return rejectWithValue({
          message: "No transaction for this date",
          retryCount: lastRetryCount,
        });
      }

      dispatch(
        showSnackbar({
          message: "Report generated successfully!",
          severity: "success",
        }),
      );

      return {
        data: mergeTransactionSearchResponses(successfulResponses),
        retryCount: lastRetryCount,
      };
    } catch (error) {
      const message =
        error?.message || "Error generating transaction search report";
      dispatch(
        showSnackbar({
          message,
          severity: "error",
        }),
      );
      return rejectWithValue({ message });
    }
  },
);
